import { Injectable } from '@angular/core';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { DeviceService } from '../device/device.service';
import { LogService } from './log-service';

const SLOW_SPAN_THRESHOLD_MS = 5000;
const PROFILE_LOG_FILENAME = 'sync_profile.txt';
const LOCAL_STORAGE_KEY = 'sync_profile_log';

export type SyncType = 'FULL_SYNC' | 'DELTA_SYNC' | 'DB_ONLY' | 'LOGIN_SYNC' | 'PRE_SYNC';

export interface SessionMetadata {
  syncType: SyncType;
  platform: string;
  osVersion: string;
  deviceInfo: string;
  networkEffectiveType: string;
  networkDownlinkMbps: number | null;
  networkRttMs: number | null;
}

export interface ProfileNode {
  operationName: string;
  startTime: number;
  endTime: number | null;
  durationMs: number | null;
  children: ProfileNode[];
  status: 'running' | 'success' | 'failed';
  errorMessage?: string;
  docCount?: number;
}

/**
 * Captures a hierarchical timing tree for sync operations.
 *
 * Disabled by default — zero overhead in production. Enable via setEnabled(true)
 * from devtools or a debug settings screen before triggering a sync.
 *
 * Each completed session is:
 *   1. Logged to the console via LogService
 *   2. Persisted with an ISO timestamp:
 *      - Cordova (iOS/Android): appended to sync_profile.txt in documentsDirectory
 *      - Browser / Electron: appended to localStorage key "sync_profile_log"
 *
 * To read the log in a browser devtools console:
 *   console.log(localStorage.getItem('sync_profile_log'))
 *
 * Usage pattern:
 *   const rootId = profiler.startSession('FULL_SYNC');
 *   const spanId  = profiler.beginSpan('accounts_sync', rootId);
 *   await accountService.syncAccounts(...).finally(() => profiler.endSpan(spanId));
 *   profiler.endSession();
 *
 * Or using the convenience wrapper:
 *   await profiler.timeAsync('accounts_sync', () => accountService.syncAccounts(...), rootId);
 */
@Injectable({
  providedIn: 'root'
})
export class SyncProfilerService {
  private enabled = false;
  private spans = new Map<string, ProfileNode>();
  private sessionRoot: ProfileNode | null = null;
  private sessionRootId: string | null = null;
  private lastSessionRoot: ProfileNode | null = null;
  private sessionMetadata: SessionMetadata | null = null;

  constructor(
    private readonly logService: LogService,
    private readonly file: File,
    private readonly deviceService: DeviceService,
  ) {}

  /** Enable or disable profiling. Defaults to false. */
  setEnabled(isEnabled: boolean): void {
    this.enabled = isEnabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Begin a new profiling session. Call once at the top of the sync entry point.
   * Returns the root span ID — pass it as parentSpanId to beginSpan or timeAsync
   * so that concurrent feature spans are correctly attached to the root.
   */
  startSession(sessionName: string, syncType: SyncType = 'DELTA_SYNC'): string {
    if (!this.enabled) { return ''; }
    this.spans.clear();
    this.sessionMetadata = this.captureSessionMetadata(syncType);
    const spanId = this.generateSpanId();
    const root: ProfileNode = {
      operationName: sessionName,
      startTime: performance.now(),
      endTime: null,
      durationMs: null,
      children: [],
      status: 'running',
    };
    this.sessionRoot = root;
    this.sessionRootId = spanId;
    this.spans.set(spanId, root);
    return spanId;
  }

  /**
   * Open a named timing span as a child of parentSpanId.
   * If parentSpanId is omitted, the span attaches to the session root.
   * Returns a spanId to pass to endSpan.
   *
   * For concurrent parallel operations (Promise.allSettled), create all spans
   * synchronously before kicking off the async work, then close each span in
   * the .finally() of its respective promise:
   *
   *   const accountsId = profiler.beginSpan('accounts_sync', rootId);
   *   await accountService.syncAccounts().finally(() => profiler.endSpan(accountsId));
   */
  beginSpan(operationName: string, parentSpanId?: string): string {
    if (!this.enabled) { return ''; }
    const spanId = this.generateSpanId();
    const node: ProfileNode = {
      operationName,
      startTime: performance.now(),
      endTime: null,
      durationMs: null,
      children: [],
      status: 'running',
    };
    const parent = parentSpanId
      ? this.spans.get(parentSpanId)
      : this.sessionRoot;
    if (parent) {
      parent.children.push(node);
    }
    this.spans.set(spanId, node);
    return spanId;
  }

  /**
   * Attach a document count to an open span. Call after beginSpan or inside a
   * timeAsyncWithSpan callback with the number of records being processed.
   */
  recordDocCount(spanId: string, count: number): void {
    if (!this.enabled) { return; }
    const node = this.spans.get(spanId);
    if (!node) { return; }
    node.docCount = count;
  }

  /**
   * Close a span. Always call this in a finally block or .finally() to ensure
   * it fires even when the operation rejects.
   */
  endSpan(spanId: string, failed = false, errorMessage?: string): void {
    if (!this.enabled) { return; }
    const node = this.spans.get(spanId);
    if (!node) { return; }
    node.endTime = performance.now();
    node.durationMs = Math.round(node.endTime - node.startTime);
    node.status = failed ? 'failed' : 'success';
    if (errorMessage) { node.errorMessage = errorMessage; }
  }

  /**
   * Convenience wrapper: times an async operation and returns its result.
   * Re-throws any error after recording the failed span — safe for use inside
   * Promise.allSettled() arrays because rejection is preserved.
   *
   * For sequential sub-operations inside a feature service, pass the parent span ID:
   *   await profiler.timeAsync('accounts_api', () => fetchApi(), featureSpanId);
   */
  async timeAsync<T>(operationName: string, operation: () => Promise<T>, parentSpanId?: string, docCount?: number): Promise<T> {
    if (!this.enabled) { return operation(); }
    const spanId = this.beginSpan(operationName, parentSpanId);
    if (docCount !== undefined) { this.recordDocCount(spanId, docCount); }
    try {
      const result = await operation();
      this.endSpan(spanId);
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.endSpan(spanId, true, message);
      throw error;
    }
  }

  /**
   * Close the session, log the timing tree, and persist it.
   * Call in the finally block of the sync entry point.
   */
  endSession(): void {
    if (!this.enabled || !this.sessionRoot || !this.sessionRootId) { return; }
    this.endSpan(this.sessionRootId);
    this.lastSessionRoot = this.sessionRoot;
    const treeString = this.buildTreeString(this.sessionRoot, '', true);
    // console, not LogService: logInfo sits below the service's default level,
    // and this must reach the device log whenever profiling is switched on —
    // which the `enabled` guard above already makes deliberate.
    console.log('[SyncProfiler]\n' + treeString);
    // Fire-and-forget: persistence failures must never block or crash the sync.
    this.persistEntry(treeString).catch(error =>
      console.warn(`[SyncProfiler] Could not persist profile entry: ${error?.message ?? error}`)
    );
    this.sessionRoot = null;
    this.sessionRootId = null;
    this.sessionMetadata = null;
    this.spans.clear();
  }

  /**
   * Like timeAsync, but passes the newly created spanId to the operation callback.
   * Use this when you need to thread the span ID into a service method so it can
   * create child sub-spans:
   *
   *   await profiler.timeAsyncWithSpan('messages_sync',
   *     (spanId) => syncMessages(data, spanId), parentId);
   */
  async timeAsyncWithSpan<T>(operationName: string, operation: (spanId: string) => Promise<T>, parentSpanId?: string): Promise<T> {
    if (!this.enabled) { return operation(''); }
    const spanId = this.beginSpan(operationName, parentSpanId);
    try {
      const result = await operation(spanId);
      this.endSpan(spanId);
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.endSpan(spanId, true, message);
      throw error;
    }
  }

  /** Returns the root node of the last completed session (useful for tests and debug UI). */
  getLastSessionResult(): ProfileNode | null {
    return this.lastSessionRoot;
  }

  /** Returns the span ID of the active session root (used to pass as parentSpanId for feature spans). */
  getSessionRootId(): string | null {
    return this.sessionRootId;
  }

  /**
   * Export all accumulated profile entries as a downloadable file.
   *
   * - Browser / Electron: triggers a browser file download of the localStorage log.
   * - Cordova: logs the path to sync_profile.txt in documentsDirectory
   *   (the file is already written there by appendToCordovaFile).
   *
   * Call this from a debug settings button or devtools after running syncs.
   */
  downloadLog(): void {
    if (this.deviceService.deviceFlags.nativeCordova) {
      this.logService.logInfo(
        `[SyncProfiler] Profile log saved at: ${this.file.documentsDirectory}${PROFILE_LOG_FILENAME}`
      );
      return;
    }
    const content = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!content) {
      this.logService.logWarning('[SyncProfiler] No profile log found in localStorage.');
      return;
    }
    const filename = `sync_profile_${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private buildEntry(treeString: string): string {
    const timestamp = new Date().toISOString();
    const meta = this.sessionMetadata;
    const metaLine = meta
      ? `SyncType: ${meta.syncType} | Device: ${meta.platform} ${meta.osVersion} | ${meta.deviceInfo} | Network: ${meta.networkEffectiveType}` +
        (meta.networkDownlinkMbps !== null ? ` ${meta.networkDownlinkMbps}Mbps` : '') +
        (meta.networkRttMs !== null ? ` rtt=${meta.networkRttMs}ms` : '')
      : 'Device: unknown';
    return `=== SYNC PROFILE ${timestamp} ===\n${metaLine}\n${treeString}\n\n`;
  }

  private captureSessionMetadata(syncType: SyncType): SessionMetadata {
    const deviceInfo = this.deviceService.deviceInfo;
    // navigator.connection is a non-standard but widely supported Network Information API.
    // It is available in WKWebView (iOS 12.2+), Chrome on Android, and Electron.
    const connection = (navigator as any).connection;
    return {
      syncType,
      platform: deviceInfo.platform ?? 'unknown',
      osVersion: deviceInfo.osVersion ?? 'unknown',
      deviceInfo: deviceInfo.info ?? 'unknown',
      networkEffectiveType: connection?.effectiveType ?? 'unknown',
      networkDownlinkMbps: connection?.downlink ?? null,
      networkRttMs: connection?.rtt ?? null,
    };
  }

  private async persistEntry(treeString: string): Promise<void> {
    // Always persist to localStorage regardless of platform.
    this.appendToLocalStorage(treeString);
    // On Cordova, also persist to the file system for easy retrieval.
    if (this.deviceService.deviceFlags.nativeCordova) {
      await this.appendToCordovaFile(treeString);
    }
  }

  /**
   * Cordova (iOS / Android): appended to sync_profile.txt in documentsDirectory.
   * Mirrors the pattern used in ErrorLogService.writeToErrorLogFile.
   */
  private async appendToCordovaFile(treeString: string): Promise<void> {
    let fileExists = false;
    try {
      await this.file.checkFile(this.file.documentsDirectory, PROFILE_LOG_FILENAME);
      fileExists = true;
    } catch {
      // File does not exist yet — writeFile will create it.
    }
    await this.file.writeFile(
      this.file.documentsDirectory,
      PROFILE_LOG_FILENAME,
      this.buildEntry(treeString),
      { append: fileExists },
    );
  }

  /**
   * Browser / Electron: each run is appended to the 'sync_profile_log' localStorage key.
   * Read all entries from devtools console: localStorage.getItem('sync_profile_log')
   * Clear entries: localStorage.removeItem('sync_profile_log')
   */
  private appendToLocalStorage(treeString: string): void {
    const existing = localStorage.getItem(LOCAL_STORAGE_KEY) ?? '';
    localStorage.setItem(LOCAL_STORAGE_KEY, existing + this.buildEntry(treeString));
  }

  private generateSpanId(): string {
    // crypto.randomUUID() is available in all environments this app targets
    // (modern browsers, WKWebView on iOS, Chrome on Android, Electron 15+)
    return crypto.randomUUID();
  }

  private buildTreeString(node: ProfileNode, prefix: string, isLastChild: boolean): string {
    const branch = prefix === '' ? '' : (isLastChild ? '└── ' : '├── ');
    const duration = node.durationMs !== null ? `(${node.durationMs}ms)` : '(running)';
    const countTag = node.docCount === undefined ? '' : ` [n=${node.docCount}]`;
    const statusTag = node.status === 'failed' ? ' [FAILED]' : '';
    const slowTag = node.durationMs !== null && node.durationMs > SLOW_SPAN_THRESHOLD_MS ? ' [SLOW]' : '';
    const header = `${prefix}${branch}${node.operationName} ${duration}${countTag}${statusTag}${slowTag}`;

    // Children indent: extend prefix based on whether current node is the last sibling.
    // Last sibling (└──) → pad with spaces; non-last sibling (├──) → pad with │ for continuation.
    const childIndent = prefix === ''
      ? ''
      : prefix + (isLastChild ? '    ' : '│   ');

    const childLines = node.children.map((child, index) =>
      this.buildTreeString(child, childIndent, index === node.children.length - 1)
    );

    return [header, ...childLines].join('\n');
  }
}
